import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig
} from "axios";
import { toast } from "react-toastify";

import { AZURE_API_BASE_URL } from "@/config/api";
import { SESSION_EXPIRED } from "@/constants/errorConstants";
import { getToken, type TokenAudience } from "@/utils/auth";

const AUTH_ERROR_STATUSES = [401, 403];

/** Set on a request once it has been replayed, so we only ever retry once. */
type RetriableConfig = InternalAxiosRequestConfig & {
  retriedAfterAuthError?: boolean;
};

/**
 * Attaches a bearer token to every request and, when the API rejects one,
 * force-refreshes the token and replays the request exactly once.
 *
 * A token can be rejected while still unexpired — revoked session, changed
 * roles or consent, clock skew — which is why the retry force-refreshes: it
 * bypasses MSAL's cached copy instead of resending the token just refused.
 * Without this, the rejected token stayed in play until the user reloaded.
 *
 * `onUnrecoverable` runs when the replay is rejected too, i.e. the failure is
 * not about a stale token and a fresh one won't fix it.
 */
const withAuth = (
  client: AxiosInstance,
  audience: TokenAudience,
  onUnrecoverable?: () => void
): AxiosInstance => {
  client.interceptors.request.use(async (config) => {
    config.headers.Authorization = `Bearer ${await getToken(audience)}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriableConfig | undefined;
      const status = error.response?.status;

      // No response at all (network error, timeout, cancellation) means there
      // is no auth verdict to react to — `error.response` is undefined here.
      if (!config || !status || !AUTH_ERROR_STATUSES.includes(status)) {
        return Promise.reject(error);
      }

      if (!config.retriedAfterAuthError) {
        config.retriedAfterAuthError = true;
        // Refresh first, then replay — the request interceptor re-runs on
        // `client.request` and picks up the newly cached token.
        await getToken(audience, { forceRefresh: true });
        return client.request(config);
      }

      toast.error(SESSION_EXPIRED);
      onUnrecoverable?.();
      return Promise.reject(error);
    }
  );

  return client;
};

export const AZURE_MJ = withAuth(
  axios.create({
    baseURL: AZURE_API_BASE_URL
  }),
  "bff"
  // Deliberately no sign-out: a persistent 403 from the BFF is an
  // authorization verdict (missing role), not an expired session.
);
